import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { fetchAdminOrders, fetchAdminStats, markProcessing, completeOrder, logout as apiLogout, fetchAdminRewards, deliverReward } from "../lib/api";
import { fmtARS, STATUS_LABEL, STATUS_DOT } from "../lib/utils-app";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, PlayCircle, CheckCircle2, ShieldAlert, Copy, Gift, Zap } from "lucide-react";
import { api, API } from "../lib/api";
import ChytaPayAdminCard from "../components/ChytaPayAdminCard";
import MaintenanceToggle from "../components/MaintenanceToggle";
import ResellersAdmin from "../components/ResellersAdmin";
import OrderFilters from "../components/OrderFilters";

const TABS = [
  { key: "paid", label: "Pagados (nuevos)" },
  { key: "pending_review", label: "Por verificar (transferencias)" },
  { key: "processing", label: "Procesando" },
  { key: "completed", label: "Completados" },
  { key: "pending_payment", label: "Pendiente pago" },
  { key: "", label: "Todos" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("paid");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [coBalance, setCoBalance] = useState(null);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", package_id: "" });

  const load = async () => {
    setBusy(true);
    try {
      const [ords, st, rw] = await Promise.all([
        fetchAdminOrders(tab || undefined, filters),
        fetchAdminStats(),
        fetchAdminRewards("pending_delivery"),
      ]);
      setOrders(ords);
      setStats(st);
      setRewards(rw);
      api.get("/admin/centralone/balance").then(({ data }) => setCoBalance(data)).catch(() => setCoBalance({ error: "Error" }));
    } catch {
      toast.error("Error cargando panel admin");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loading, user, filters]);

  if (loading) return null;
  if (!user || !user.is_admin) {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const startLogin = () => {
      const redirectUrl = window.location.origin + "/admin";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <ShieldAlert className="w-12 h-12 text-[#ff4500] mx-auto mb-4" />
        <h1 className="font-display text-4xl tracking-wider">Acceso restringido</h1>
        {!user ? (
          <>
            <p className="text-neutral-400 mt-2">
              Iniciá sesión con tu cuenta de admin autorizada para ver los pedidos y ejecutar recargas.
            </p>
            <button
              onClick={startLogin}
              data-testid="admin-login-btn"
              className="btn-primary mt-6 px-5 py-3 rounded-md"
            >
              Iniciar sesión con Google
            </button>
          </>
        ) : (
          <>
            <p className="text-neutral-400 mt-3">
              Estás logueado como <span className="text-white font-semibold">{user.email}</span>, pero esa cuenta no es admin.
            </p>
            <p className="text-xs text-neutral-500 mt-2">
              El admin autorizado es la cuenta de Google que configuraste al crear el sitio.
              Cerrá sesión y volvé a entrar con esa cuenta.
            </p>
            <button
              onClick={async () => {
                await apiLogout();
                window.location.reload();
              }}
              data-testid="admin-logout-relogin-btn"
              className="btn-primary mt-6 px-5 py-3 rounded-md"
            >
              Cerrar sesión y reintentar
            </button>
          </>
        )}
      </div>
    );
  }

  async function onProcess(id) {
    try {
      await markProcessing(id);
      toast.success("Marcado como procesando");
      // Open pinxtoregames in a new tab for the admin to execute
      window.open("https://pinxtoregames.com/perfil#recargas-directas", "_blank", "noopener");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  async function onAutoDispatch(id) {
    try {
      const { data } = await api.post(`/admin/orders/${id}/auto-dispatch`);
      if (data.ok) toast.success("Recarga enviada a Central One");
      else toast.error(data.error || "Falló el envío automático");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  async function onReject(id) {
    const note = window.prompt("Motivo del rechazo (lo ve el cliente)", "Comprobante inválido");
    if (note === null) return;
    try {
      await api.post(`/admin/orders/${id}/reject`, { admin_note: note });
      toast.success("Pedido rechazado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  async function onSync(id) {
    try {
      const { data } = await api.post(`/admin/orders/${id}/sync`);
      toast.success(`Estado Central One: ${data.external_status || "sin datos"}`);
      load();
    } catch {
      toast.error("Error sincronizando");
    }
  }

  async function onComplete(id) {
    const note = window.prompt("Nota opcional para el cliente (transacción, comprobante...)", "");
    if (note === null) return;
    try {
      await completeOrder(id, note);
      toast.success("Pedido completado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] text-[#ff4500]">PANEL</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-wider">Admin · Recargas</h1>
          </div>
          <button onClick={load} data-testid="admin-refresh-btn" className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatCard label="Pedidos totales" value={stats.total_orders} />
            <StatCard label="Ingresos (aprobados)" value={fmtARS(stats.total_revenue_ars)} />
            <StatCard label="Pagados a procesar" value={stats.by_status?.paid?.count || 0} highlight />
            <StatCard label="Completados" value={stats.by_status?.completed?.count || 0} />
            <div data-testid="centralone-balance">
              <StatCard
                label="Saldo Central One"
                value={!coBalance ? "..." : coBalance.error ? "Error" : coBalance.configured === false ? "Sin clave" : `USD ${Number(coBalance.available_balance).toFixed(2)}`}
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <MaintenanceToggle />
        <ChytaPayAdminCard />
        <ResellersAdmin />
        <div className="flex flex-wrap gap-2 mb-4 border-b border-white/10 pb-3">
          {TABS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key || "all"}`}
              className={`px-3 py-2 rounded-md text-sm ${tab === t.key ? "bg-[#ff4500] text-black" : "bg-white/5 hover:bg-white/10 text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <OrderFilters value={filters} onChange={setFilters} count={orders.length} totalArs={orders.reduce((s, o) => s + (o.amount_ars || 0), 0)} />
        {busy ? (
          <div className="text-neutral-400">Cargando...</div>
        ) : orders.length === 0 ? (
          <div className="rounded-xl border border-white/10 p-8 text-center bg-[#0d0d0d] text-neutral-400">
            No hay pedidos en esta categoría.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0d0d0d] text-neutral-400">
                <tr>
                  <th className="text-left px-3 py-2">Pedido</th>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2">Player ID</th>
                  <th className="text-right px-3 py-2">Cant.</th>
                  <th className="text-right px-3 py-2">Monto</th>
                  <th className="text-left px-3 py-2">Estado</th>
                  <th className="text-right px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} data-testid={`admin-row-${o.order_id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-3 py-3">
                      <div className="font-mono text-xs text-neutral-300">{o.order_id}</div>
                      <div className="text-[11px] text-neutral-500">{new Date(o.created_at).toLocaleString("es-AR")}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-white text-xs">{o.user_email}</div>
                      {o.player_nickname && <div className="text-[11px] text-neutral-500">{o.player_nickname}</div>}
                    </td>
                    <td className="px-3 py-3 font-mono">
                      <div className="flex items-center gap-1.5">
                        <span>{o.player_id}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(o.player_id);
                            toast.success("ID copiado");
                          }}
                          data-testid={`copy-id-${o.order_id}`}
                          className="p-1 rounded hover:bg-white/10 text-neutral-400 hover:text-[#ff4500]"
                          aria-label="Copiar Player ID"
                          title="Copiar"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-display text-lg tracking-wider">{o.diamonds ? o.diamonds.toLocaleString("es-AR") : `${o.quantity} × ${o.product_name}`}</td>
                    <td className="px-3 py-3 text-right">{fmtARS(o.amount_ars)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-block w-2 h-2 mr-2 rounded-full ${STATUS_DOT[o.status]}`} />
                      <span className={`status-${o.status}`}>{STATUS_LABEL[o.status]}</span>
                      {o.fulfillment_provider === "centralone" && (
                        <div className="text-[10px] text-sky-300 mt-0.5" data-testid={`auto-status-${o.order_id}`}>
                          Auto: {o.fulfillment_external_status || (o.fulfillment_external_id ? "enviado" : "falló")}
                        </div>
                      )}
                      {o.admin_note && <div className="text-[10px] text-neutral-500 mt-0.5 max-w-[220px]">{o.admin_note}</div>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2 justify-end">
                        {o.status === "paid" && o.category === "diamonds" && (
                          <button
                            onClick={() => onAutoDispatch(o.order_id)}
                            data-testid={`auto-dispatch-${o.order_id}`}
                            className="px-2.5 py-1.5 rounded-md bg-amber-400/90 hover:bg-amber-300 text-black text-xs flex items-center gap-1"
                          >
                            <Zap className="w-3 h-3" /> Auto
                          </button>
                        )}
                        {o.status === "processing" && o.fulfillment_external_id && (
                          <button
                            onClick={() => onSync(o.order_id)}
                            data-testid={`sync-${o.order_id}`}
                            className="px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" /> Sincronizar
                          </button>
                        )}
                        {o.status === "paid" && (
                          <button
                            onClick={() => onProcess(o.order_id)}
                            data-testid={`process-${o.order_id}`}
                            className="px-2.5 py-1.5 rounded-md bg-sky-500/90 hover:bg-sky-400 text-black text-xs flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" /> Procesar
                          </button>
                        )}
                        {o.status === "pending_review" && o.receipt_file_id && (
                          <a
                            href={`${API}/files/${o.receipt_file_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`receipt-${o.order_id}`}
                            className="px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" /> Comprobante
                          </a>
                        )}
                        {(o.status === "pending_review" || (o.source === "reseller" && ["paid", "processing"].includes(o.status))) && (
                          <button
                            onClick={() => onReject(o.order_id)}
                            data-testid={`reject-${o.order_id}`}
                            className="px-2.5 py-1.5 rounded-md bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 text-xs"
                          >
                            {o.source === "reseller" ? "Rechazar y devolver saldo" : "Rechazar"}
                          </button>
                        )}
                        {(o.status === "paid" || o.status === "processing" || o.status === "pending_review") && (
                          <button
                            onClick={() => onComplete(o.order_id)}
                            data-testid={`complete-${o.order_id}`}
                            className="btn-primary px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3 h-3" /> Completar
                          </button>
                        )}
                        {o.status === "pending_payment" && o.checkout_url && (
                          <a
                            href={o.checkout_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1"
                          >
                            <PlayCircle className="w-3 h-3" /> Link pago
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Rewards pending delivery */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-xs tracking-[0.3em] text-amber-300 flex items-center gap-1">
                <Gift className="w-3 h-3" /> RECOMPENSAS
              </div>
              <h2 className="font-display text-3xl tracking-wider">Cajas canjeadas · pendientes</h2>
            </div>
            <div className="text-sm text-neutral-400">{rewards.length} pendientes</div>
          </div>
          {rewards.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-6 text-center text-neutral-500 text-sm">
              No hay recompensas pendientes de entrega.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-amber-400/30">
              <table className="min-w-full text-sm">
                <thead className="bg-[#0d0d0d] text-neutral-400">
                  <tr>
                    <th className="text-left px-3 py-2">Recompensa</th>
                    <th className="text-left px-3 py-2">Cliente</th>
                    <th className="text-left px-3 py-2">Player ID</th>
                    <th className="text-left px-3 py-2">Premio</th>
                    <th className="text-left px-3 py-2">Rareza</th>
                    <th className="text-right px-3 py-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {rewards.map((r) => (
                    <tr key={r.reward_id} data-testid={`reward-row-${r.reward_id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-3 py-3">
                        <div className="font-mono text-xs text-neutral-300">{r.reward_id}</div>
                        <div className="text-[11px] text-neutral-500">{new Date(r.created_at).toLocaleString("es-AR")}</div>
                      </td>
                      <td className="px-3 py-3 text-white text-xs">{r.user_email}</td>
                      <td className="px-3 py-3 font-mono">
                        {r.player_id ? (
                          <div className="flex items-center gap-1.5">
                            <span>{r.player_id}</span>
                            <button
                              onClick={() => {
                                navigator.clipboard?.writeText(r.player_id);
                                toast.success("ID copiado");
                              }}
                              data-testid={`copy-reward-id-${r.reward_id}`}
                              className="p-1 rounded hover:bg-white/10 text-neutral-400 hover:text-amber-300"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : <span className="text-neutral-500">—</span>}
                      </td>
                      <td className="px-3 py-3 text-white">{r.prize_label}</td>
                      <td className="px-3 py-3">
                        <span className="text-xs uppercase tracking-widest text-amber-300">{r.rarity}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          onClick={async () => {
                            const note = window.prompt("Nota opcional (opcional):", "") || "";
                            try {
                              await deliverReward(r.reward_id, note);
                              toast.success("Recompensa marcada como entregada");
                              load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Error");
                            }
                          }}
                          data-testid={`deliver-${r.reward_id}`}
                          className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-1 ml-auto"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Marcar entregado
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "border-[#ff4500] bg-[#1a0d05]" : "border-white/10 bg-[#0d0d0d]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="mt-1 font-display text-3xl tracking-wider">{value}</div>
    </div>
  );
}
